import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Navigation, Phone } from "lucide-react";
import { Reveal, SectionHeading, Overline } from "./primitives";
import { WHY_A3, OPERATING_MODEL, AI_FEATURES, EXTENDED_TEAM, TIMELINE, CONTACT, IMAGES } from "@/data/site";
import { useModal } from "./Forms";

/* ---------------- Why A3 ---------------- */
export function WhyA3() {
  return (
    <section className="relative bg-ink-1 py-28" data-testid="why-a3">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline="06 — Why A3"
          title={<>WHY INSTITUTIONS <span className="text-gradient">CHOOSE A3.</span></>}
        />
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_A3.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.08}>
              <motion.div whileHover={{ y: -5 }} className="group h-full rounded-3xl border border-white/10 bg-ink-2 p-7 transition-colors duration-300 hover:border-brand-cyan/40">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-cyan/12 text-brand-cyan transition-transform duration-300 group-hover:scale-110">
                  <c.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-white">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/50">{c.desc}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Operating Model ---------------- */
export function OperatingModel() {
  return (
    <section className="relative overflow-hidden bg-ink-0 py-28" data-testid="operating-model">
      <div className="absolute inset-0 bg-radial-glow opacity-50" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline="07 — Operating Model"
          title={<>WE DON'T JUST GENERATE LEADS. WE BUILD THE <span className="text-gradient">SYSTEM THAT CONVERTS THEM.</span></>}
        />
        <div className="mt-16 flex flex-wrap items-center justify-center gap-3">
          {OPERATING_MODEL.map((step, i) => (
            <React.Fragment key={step}>
              <Reveal delay={i * 0.05}>
                <div className="rounded-full border border-white/10 bg-ink-1 px-5 py-3 font-mono text-xs uppercase tracking-wider text-white/80 transition-colors hover:border-brand-cyan/40 hover:text-brand-cyan">
                  {step}
                </div>
              </Reveal>
              {i < OPERATING_MODEL.length - 1 && (
                <ArrowRight className="h-4 w-4 shrink-0 text-brand-cyan/50" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- AI + Technology ---------------- */
export function AITech() {
  return (
    <section className="relative overflow-hidden bg-ink-1 py-28" data-testid="ai-tech">
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Overline>08 — AI &amp; Technology</Overline>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl">
              THE NEXT GENERATION OF EDUCATION GROWTH IS <span className="text-gradient">INTELLIGENT.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/55">
              We combine trained teams with AI-enabled technology across the entire funnel — from lead qualification to campaign intelligence and dashboard reporting.
            </p>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="mb-6 overflow-hidden rounded-3xl border border-white/10">
                <img src={IMAGES.aiTech} alt="AI-powered education growth technology" className="h-52 w-full object-cover" loading="lazy" data-testid="ai-tech-image" />
              </div>
            </Reveal>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {AI_FEATURES.map((f, i) => (
                <Reveal key={f} delay={(i % 3) * 0.06}>
                  <div className="group flex h-full items-center gap-3 rounded-2xl border border-white/10 bg-ink-2 p-4 transition-colors hover:border-brand-cyan/40">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-brand-cyan shadow-[0_0_10px_rgba(198,255,58,0.8)]" />
                    <span className="text-sm font-medium text-white/80">{f}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Extended Team ---------------- */
export function ExtendedTeam() {
  return (
    <section className="relative bg-ink-0 py-28" data-testid="extended-team">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline="Your Extended Team"
          title={<>DON'T BUILD 10 DIFFERENT TEAMS. BUILD ONE <span className="text-gradient">STRATEGIC PARTNERSHIP.</span></>}
          align="center"
          className="mx-auto"
        />
        <div className="relative mt-16">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {EXTENDED_TEAM.map((t, i) => (
              <Reveal key={t} delay={(i % 4) * 0.06}>
                <div className="rounded-2xl border border-white/10 bg-ink-1 p-5 text-center transition-colors hover:border-brand-cyan/40">
                  <p className="font-display text-base font-semibold text-white">{t}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <ArrowRight className="h-6 w-6 rotate-90 text-brand-cyan/50" />
          </div>
          <Reveal delay={0.2}>
            <div className="mx-auto mt-8 w-fit rounded-3xl border border-brand-cyan/30 bg-gradient-to-br from-brand-blue/25 to-ink-1 px-12 py-8 text-center">
              <p className="font-display text-3xl font-bold text-white">A3 <span className="text-gradient">GLOBAL</span></p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">One Strategic Partnership</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Company Story + Timeline ---------------- */
export function CompanyStory() {
  return (
    <section id="about-story" className="relative overflow-hidden bg-ink-1 py-28" data-testid="company-story">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Overline>Company Story</Overline>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl">
              BUILT FOR EDUCATION. <span className="text-gradient">BUILT FOR SCALE.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-white/55">
              Since 2015, A3 Global Marketing &amp; Education has been working within the education ecosystem, helping institutions navigate the changing world of digital marketing, technology, student acquisition and admissions.
            </p>
            <p className="mt-4 text-base leading-relaxed text-white/55">
              Today, A3 combines education expertise, technology, marketing capabilities and a 100+ professional team to create integrated growth solutions for colleges and universities.
            </p>
            <Reveal delay={0.15}>
              <div className="relative mt-8 overflow-hidden rounded-3xl border border-white/10">
                <img src={IMAGES.team} alt="A3 Global team collaborating" className="h-56 w-full object-cover" loading="lazy" data-testid="story-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-1/70 to-transparent" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:flex lg:flex-col lg:justify-center">
            <div className="relative pl-8">
              <div className="absolute left-0 top-2 h-full w-px bg-gradient-to-b from-brand-cyan via-brand-blue to-transparent" />
              {TIMELINE.map((t, i) => (
                <Reveal key={t.year} delay={i * 0.06}>
                  <div className="relative mb-8 last:mb-0">
                    <div className="absolute -left-[34px] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-brand-cyan bg-ink-1" />
                    <div className="flex items-baseline gap-4">
                      <span className="font-display text-2xl font-bold text-brand-cyan">{t.year}</span>
                      <span className="font-display text-lg text-white">{t.title}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Office / Map ---------------- */
export function Office() {
  const { openContact } = useModal();
  return (
    <section id="contact" className="relative bg-ink-0 py-28" data-testid="office">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading overline="Our Home" title={<>VISIT US IN <span className="text-gradient">LUCKNOW.</span></>} />
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-ink-1 p-8">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-cyan/12 text-brand-cyan">
                  <MapPin className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold text-white">A3 Global Head Office</h3>
                <p className="mt-3 max-w-sm text-base leading-relaxed text-white/60">{CONTACT.address}</p>
                <a href={`tel:${CONTACT.phone}`} className="mt-4 inline-flex items-center gap-2 text-sm text-white/70 hover:text-brand-cyan">
                  <Phone className="h-4 w-4 text-brand-cyan" /> {CONTACT.phoneDisplay}
                </a>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button onClick={openContact} className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-6 py-3 font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_25px_rgba(198,255,58,0.4)]" data-testid="office-contact">
                  Contact Us
                </button>
                <a href={CONTACT.mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 font-medium text-white transition-colors hover:border-brand-cyan/50">
                  <Navigation className="h-4 w-4 text-brand-cyan" /> Google Maps
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative h-[340px] overflow-hidden rounded-3xl border border-white/10 lg:h-full">
              <iframe
                title="A3 Global Office Map"
                src="https://www.google.com/maps?q=Hazratganj+Lucknow&output=embed"
                className="h-full w-full grayscale-[0.3] invert-[0.9] hue-rotate-180"
                loading="lazy"
              />
              <div className="pointer-events-none absolute inset-0 bg-brand-blue/10 mix-blend-overlay" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Final CTA ---------------- */
export function FinalCTA() {
  const { openInstitution, openContact } = useModal();
  return (
    <section className="relative overflow-hidden bg-ink-0 py-32" data-testid="final-cta">
      <div
        className="absolute inset-0 opacity-45"
        style={{ backgroundImage: `url(${IMAGES.ctaBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink-0/80 via-ink-0/55 to-ink-0/85" />
      <div className="absolute inset-0 bg-grid opacity-25" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-blue/20 blur-[150px]" />
      <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
        <Reveal>
          <h2 className="font-display text-5xl font-bold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
            YOUR COLLEGE.<br />YOUR VISION.<br /><span className="text-gradient">OUR GROWTH ENGINE.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mx-auto mt-6 max-w-xl text-base text-white/60 md:text-lg">
            Let's build the next generation of education together.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            <button onClick={openInstitution} className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-8 py-4 font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_35px_rgba(198,255,58,0.5)]" data-testid="final-partner">
              Partner With A3 <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={openContact} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-4 font-medium text-white transition-colors hover:border-brand-cyan/50" data-testid="final-talk">
              Talk to Our Team
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
